<template>
  <v-card>
    <v-card-title class="d-flex align-center flex-wrap">
      <span>EuroNCAP Pelastuskortit</span>
      <v-spacer />
      <span v-if="dbInfo.updatedAt" class="text-caption grey--text mr-2">
        Päivitetty {{ $formatDateAndHourMinute(dbInfo.updatedAt) }} ·
        {{ dbInfo.count }} ajoneuvoa
      </span>
      <v-btn
        v-if="$hasRole('hallitus')"
        small
        outlined
        color="primary"
        :loading="syncing"
        @click="syncDatabase"
      >
        <v-icon left small>mdi-database-sync</v-icon>
        Päivitä tietokanta
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-breadcrumbs class="pl-0" style="flex-wrap: wrap">
        <v-breadcrumbs-item :disabled="!selectedMake" @click.native="goToMakes">
          Merkit
        </v-breadcrumbs-item>
        <template v-if="selectedMake">
          <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
          <v-breadcrumbs-item
            :disabled="!selectedModel"
            @click.native="goToModels"
          >
            {{ selectedMake }}
          </v-breadcrumbs-item>
        </template>
        <template v-if="selectedModel">
          <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
          <v-breadcrumbs-item disabled>
            {{ selectedModel }}
          </v-breadcrumbs-item>
        </template>
      </v-breadcrumbs>

      <v-alert v-if="notSynced" type="info" text dense>
        Pelastuskorttitietokantaa ei ole vielä synkronoitu.
        <span v-if="$hasRole('hallitus')"
          >Paina "Päivitä tietokanta" yllä.</span
        >
        <span v-else>Pyydä hallituksen jäsentä synkronoimaan se.</span>
      </v-alert>

      <div v-if="loadingMakes" class="text-center py-6">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <!-- Level 1: makes -->
      <template v-else-if="!selectedMake">
        <v-text-field
          v-model="makeFilter"
          label="Suodata merkin mukaan"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          clearable
          class="mb-2"
        />
        <v-row>
          <v-col
            v-for="make in filteredMakes"
            :key="make"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <v-card
              outlined
              class="pa-4 text-center browse-card d-flex align-center"
              style="min-height: 76px"
              @click="selectMake(make)"
            >
              <v-avatar tile size="60" class="mr-3">
                <v-img
                  v-if="makeLogoUrl(make) && !brokenLogos[make]"
                  :src="proxiedLogos[make] || makeLogoUrl(make)"
                  contain
                  @error="onLogoError(make)"
                />
                <v-icon v-else size="40" color="grey lighten-1"
                  >mdi-car-side</v-icon
                >
              </v-avatar>
              <span class="text-body-1 font-weight-medium">{{ make }}</span>
            </v-card>
          </v-col>
        </v-row>
        <div
          v-if="!loadingMakes && filteredMakes.length === 0 && !notSynced"
          class="text-center grey--text py-4"
        >
          Ei tuloksia
        </div>
      </template>

      <!-- Level 2: models -->
      <template v-else-if="!selectedModel">
        <div v-if="loadingModels" class="text-center py-6">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <v-row v-else>
          <v-col v-for="m in models" :key="m.model" cols="12" sm="6" md="6">
            <v-card
              outlined
              class="pa-3 browse-card d-flex align-center"
              style="min-height: 220px"
              @click="selectModel(m.model)"
            >
              <div class="model-thumb-grid mr-3">
                <div
                  v-for="(url, i) in modelPictures(m)"
                  :key="i"
                  class="model-thumb-cell"
                >
                  <v-img
                    v-if="url && !brokenModelImages[modelImageKey(m.model, i)]"
                    :src="proxiedModelImages[modelImageKey(m.model, i)] || url"
                    contain
                    @error="onModelImageError(m.model, i, url)"
                  />
                  <v-icon v-else size="48" color="grey lighten-1"
                    >mdi-car-side</v-icon
                  >
                </div>
              </div>
              <div>
                <div class="text-body-1 font-weight-bold">{{ m.model }}</div>
                <div class="text-body-2 grey--text mt-1">
                  {{ m.count }} versiota · {{ m.yearFrom }}–{{
                    m.yearUntil || ""
                  }}
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
        <div
          v-if="!loadingModels && models.length === 0"
          class="text-center grey--text py-4"
        >
          Malleja ei löytynyt
        </div>
      </template>

      <!-- Level 3: variants -->
      <template v-else>
        <div v-if="loadingVariants" class="text-center py-6">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <v-row v-else>
          <v-col
            v-for="v in variants"
            :key="v.id"
            cols="12"
            sm="6"
            md="6"
            lg="4"
          >
            <v-card outlined class="pa-2">
              <div
                v-if="
                  v.pictureUrl && !proxiedImages[v.id] && !brokenImages[v.id]
                "
                class="d-flex align-center justify-center"
                style="height: 220px"
              >
                <v-progress-circular indeterminate color="primary" />
              </div>
              <v-img
                v-else-if="proxiedImages[v.id]"
                :src="proxiedImages[v.id]"
                height="220"
                contain
              />
              <div
                v-else
                class="d-flex align-center justify-center"
                style="height: 220px"
              >
                <v-icon size="80" color="grey lighten-1">mdi-car-side</v-icon>
              </div>
              <v-card-text class="pa-2 text-center">
                <div class="text-body-1 font-weight-bold">
                  {{ v.name }}
                </div>
                <div class="text-body-2">
                  {{ v.bodyType }} · {{ v.buildYearFrom }}–{{
                    v.buildYearUntil || ""
                  }}
                  <template v-if="v.doors">· {{ v.doors }} ovea</template>
                </div>
                <v-btn
                  v-if="v.rescueSheetUrl"
                  small
                  color="primary"
                  class="mt-2"
                  @click="openRescueSheet(v)"
                >
                  <v-icon left small>mdi-file-pdf-box</v-icon>
                  Avaa pelastuskortti
                </v-btn>
                <div v-else class="text-caption grey--text mt-2">
                  Ei pelastuskorttia saatavilla
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <div
          v-if="!loadingVariants && variants.length === 0"
          class="text-center grey--text py-4"
        >
          Versioita ei löytynyt
        </div>
      </template>
    </v-card-text>

    <pelastuskortti-dialog
      v-model="viewerDialog"
      :url="viewerUrl"
      :title="viewerTitle"
    />
  </v-card>
</template>

<script>
import api from "@/api";
import PelastuskorttiDialog from "@/components/PelastuskorttiDialog";
import {
  fetchRescueMetadata,
  fetchRescueModelsByMake,
  fetchRescueVariantsByModel,
} from "@/utils/pelastuskortitFirestore";

export default {
  name: "PelastuskorttiSelain",
  components: { PelastuskorttiDialog },
  data: () => ({
    viewerDialog: false,
    viewerUrl: "",
    viewerTitle: "",
    loadingMakes: false,
    loadingModels: false,
    loadingVariants: false,
    syncing: false,
    notSynced: false,
    dbInfo: { count: 0, updatedAt: null, makeLogos: {} },
    makes: [],
    makeFilter: "",
    models: [],
    variants: [],
    selectedMake: null,
    selectedModel: null,
    brokenImages: {},
    proxiedImages: {},
    brokenLogos: {},
    proxiedLogos: {},
    brokenModelImages: {},
    proxiedModelImages: {},
  }),
  computed: {
    filteredMakes() {
      const filter = (this.makeFilter || "").trim().toLowerCase();
      if (!filter) return this.makes;
      return this.makes.filter((m) => m.toLowerCase().includes(filter));
    },
  },
  mounted() {
    this.loadMakes();
  },
  methods: {
    makeLogoUrl(make) {
      const logo = this.dbInfo.makeLogos && this.dbInfo.makeLogos[make];
      return (logo && logo.logoUrl) || null;
    },
    modelPictures(m) {
      // Always return 4 slots so the grid stays a fixed 2x2 shape even with fewer pictures.
      const urls = (m.pictureUrls || []).slice(0, 4);
      while (urls.length < 4) urls.push(null);
      return urls;
    },
    modelImageKey(model, i) {
      return `${model}__${i}`;
    },
    async loadMakes() {
      this.loadingMakes = true;
      this.notSynced = false;
      try {
        const result = await fetchRescueMetadata();
        if (!result.exists || !result.makes || result.makes.length === 0) {
          this.notSynced = true;
          return;
        }
        this.makes = result.makes;
        this.dbInfo = {
          count: result.count,
          updatedAt: result.updatedAt,
          makeLogos: result.makeLogos || {},
        };
      } catch (e) {
        // Fallback to backend cloud function
        try {
          const result = (await api.rescueDatabaseMakes()).data;
          this.makes = result.makes;
          this.dbInfo = {
            count: result.count,
            updatedAt: result.updatedAt,
            makeLogos: result.makeLogos || {},
          };
        } catch (fbErr) {
          if (
            fbErr.code === "failed-precondition" ||
            /synkronoitu/.test(fbErr.message || "")
          ) {
            this.notSynced = true;
          } else {
            this.$toast.error(fbErr.message || "Merkkien haku epäonnistui");
          }
        }
      } finally {
        this.loadingMakes = false;
      }
    },
    async selectMake(make) {
      this.selectedMake = make;
      this.selectedModel = null;
      this.models = [];
      this.loadingModels = true;
      try {
        const { models } = await fetchRescueModelsByMake(make);
        this.models = models;
      } catch (e) {
        try {
          const result = (await api.rescueDatabaseModels({ make })).data;
          this.models = result.models;
        } catch (fbErr) {
          this.$toast.error(fbErr.message || "Mallien haku epäonnistui");
        }
      } finally {
        this.loadingModels = false;
      }
    },
    async selectModel(model) {
      this.selectedModel = model;
      this.variants = [];
      this.loadingVariants = true;
      try {
        const { variants } = await fetchRescueVariantsByModel(
          this.selectedMake,
          model,
        );
        this.variants = variants;
        this.loadVariantImages(this.variants);
      } catch (e) {
        try {
          const result = (
            await api.rescueDatabaseVariants({
              make: this.selectedMake,
              model,
            })
          ).data;
          this.variants = result.variants;
          this.loadVariantImages(this.variants);
        } catch (fbErr) {
          this.$toast.error(fbErr.message || "Versioiden haku epäonnistui");
        }
      } finally {
        this.loadingVariants = false;
      }
    },
    goToMakes() {
      this.selectedMake = null;
      this.selectedModel = null;
    },
    goToModels() {
      this.selectedModel = null;
    },
    async syncDatabase() {
      this.syncing = true;
      try {
        const result = (await api.rescueDatabaseSync()).data;
        this.$toast.success(
          `Tietokanta päivitetty (${result.count} ajoneuvoa, ${result.makesCount} merkkiä)`,
        );
        await this.loadMakes();
      } catch (e) {
        this.$toast.error(e.message || "Tietokannan päivitys epäonnistui");
      } finally {
        this.syncing = false;
      }
    },
    loadVariantImages(variants) {
      (variants || []).forEach((v) => {
        if (
          v &&
          v.pictureUrl &&
          !this.proxiedImages[v.id] &&
          !this.brokenImages[v.id]
        ) {
          this.fetchVariantImage(v);
        }
      });
    },
    async fetchVariantImage(variant) {
      if (!variant?.pictureUrl) return;
      if (this.proxiedImages[variant.id]) return;
      try {
        const result = (await api.fetchImage({ url: variant.pictureUrl })).data;
        this.$set(
          this.proxiedImages,
          variant.id,
          `data:${result.mimeType};base64,${result.base64}`,
        );
      } catch (e) {
        this.$set(this.brokenImages, variant.id, true);
      }
    },
    async onLogoError(make) {
      // Same mislabeled-content-type / ORB issue as onImageError, but for make logos.
      if (this.proxiedLogos[make]) {
        this.$set(this.brokenLogos, make, true);
        return;
      }
      try {
        const result = (await api.fetchImage({ url: this.makeLogoUrl(make) }))
          .data;
        this.$set(
          this.proxiedLogos,
          make,
          `data:${result.mimeType};base64,${result.base64}`,
        );
      } catch (e) {
        this.$set(this.brokenLogos, make, true);
      }
    },
    async onModelImageError(model, i, url) {
      // Same mislabeled-content-type / ORB issue as onImageError, but for model preview thumbnails.
      const key = this.modelImageKey(model, i);
      if (this.proxiedModelImages[key]) {
        this.$set(this.brokenModelImages, key, true);
        return;
      }
      try {
        const result = (await api.fetchImage({ url })).data;
        this.$set(
          this.proxiedModelImages,
          key,
          `data:${result.mimeType};base64,${result.base64}`,
        );
      } catch (e) {
        this.$set(this.brokenModelImages, key, true);
      }
    },
    openRescueSheet(v) {
      if (!v || !v.rescueSheetUrl) return;
      const years = [v.buildYearFrom, v.buildYearUntil]
        .filter(Boolean)
        .join("–");
      const name = [
        this.selectedMake,
        v.modelName || v.name,
        years ? `(${years})` : "",
      ]
        .filter(Boolean)
        .join(" ");
      this.viewerTitle = `Pelastuskortti: ${name}`;
      this.viewerUrl = v.rescueSheetUrl;
      this.viewerDialog = true;
    },
  },
};
</script>

<style scoped>
.browse-card {
  cursor: pointer;
  height: 100%;
}
.browse-card:hover {
  border-color: #6c0000;
}
.model-thumb-grid {
  display: grid;
  grid-template-columns: repeat(2, 100px);
  grid-template-rows: repeat(2, 100px);
  gap: 4px;
  flex-shrink: 0;
}
.model-thumb-cell {
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
}
</style>
